import React, { useEffect, useRef, useState } from 'react';
import DrawerFooter from 'cui/components/rightSideDrawer/DrawerFooter';
import { showSuccessSnackbar } from 'cui/components/snackbar/Snackbar.slice';
import {
	Form,
	Item,
	Label
} from 'devextreme-react/form';
import { useInsightApi } from 'hooks/use-insight-api';
import { useAppDispatch, useAppSelector } from 'store/app/hooks';
import { RootState } from 'store/app/store';
import './applications.scss';
import { refreshApplications } from '../../../store/app/applications.slice';
import { handleImageExtensionValidation } from './application.validation';
import { ApplicationDto } from 'api/NSwagApi/api';
import { LoadIndicator } from 'devextreme-react/load-indicator'; 
import { setBannerCount } from 'cui/components/breadcrumb/breadcrumb.slice';
import ErrorMessage from 'cui/components/error/DisplayError';

type applicationRegisterProps = {
	onCancelClick: any,
	onRegister: () => void
};

type FormData = {
	applicationId: string;
	applicationName: string;
	adminFeature: string,
	viewFeature: string,
	executiveFeature: string,
	isActive: boolean;
	file: File;
}

export type ItemOptions = {
	displayExpr?: string;
	items?: any[];
	value: any;
	valueExpr?: string;
	searchEnabled?: boolean;
}

const defaultData: FormData = {
	applicationId: '',
	applicationName: '',
	adminFeature: '',
	viewFeature: '',
	executiveFeature: '',
	isActive: false,
	file: File.prototype
};

export const ApplicationRegister: React.FunctionComponent<applicationRegisterProps> = (
	props
) => {
	const dispatch = useAppDispatch();
	const { registerApplication, fetchRegisteredApplicationsList, fetchUnregisteredApplicationsList } = useInsightApi();
	const formRef = useRef<Form>(null);
	const logoRef = useRef<HTMLInputElement>(null);
	const [formData, setFormData] = useState<FormData>({...defaultData});
	const [error, setError] = useState<any>({});
	const [unregisteredApplications, setUnregisteredApplications] = useState<ApplicationDto[]>([]);
	const [fileName, setFileName] = useState('');
	const [isApplicationIdEmpty, setIsApplicationIdEmpty] = useState<boolean>(true);
	const [hasEnteredApplicationId, setHasEnteredApplicationId] = useState<boolean>(false);
	const [isDisplayNameEmpty, setIsDisplayNameEmpty] = useState<boolean>(true);
	const [isDisplayNameValid, setIsDisplayNameValid] = useState<boolean>(false);
	const [hasEnteredDisplayName, setHasEnteredDisplayName] = useState<boolean>(false);
	const [isAdminFeatureEmpty, setIsAdminFeatureEmpty] = useState<boolean>(true);
	const [hasEnteredAdminFeature, setHasEnteredAdminFeature] = useState<boolean>(false);
	const [isViewApplicationFeatureEmpty, setIsViewApplicationFeatureEmpty] = useState<boolean>(true);
	const [hasEnteredViewApplicationFeature, setHasEnteredViewApplicationFeature] = useState<boolean>(false);
	const [hasEnteredExecutiveReportsFeature, setHasEnteredExecutiveReportsFeature] = useState<boolean>(false);
	const [isValidFileExtension, setIsValidFileExtension] = useState<boolean>(true);
	const [currentlySubmitting, setCurrentlySubmitting] = useState<boolean>(false);
	
	const cemFeatures = useAppSelector(
		(state: RootState) => state.cem.roleFeatures
	);
	
	const sortUnregisteredApplications = (unregisteredApps: ApplicationDto[]) => {
		return unregisteredApps.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
	};

	const sortCemFeatures = () => {
		const cemFeaturesCopy = [...cemFeatures];
		return cemFeaturesCopy.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
	};
	const sortedFeatures = sortCemFeatures();

	const applicationIdOptions = {
		displayExpr: 'name',
		items: unregisteredApplications,
		value: formData.applicationId,
		valueExpr: 'identifier',
		searchEnabled: true,
		onValueChanged: (e: { value: string; }) => validateApplicationId(e.value)
	};

	const applicationDisplayNameOptions = { 
		value: formData.applicationName,
		onValueChanged: (e: { value: string; }) => validateDisplayName(e.value)
	};

	const featuresCreationAdminOptions = {
		displayExpr: 'name',
		items: sortedFeatures,
		value: formData.adminFeature,
		valueExpr: 'name',
		searchEnabled: true,
		onValueChanged: (e: { value: string; }) => validateAdminFeature(e.value)
	};
	const featuresCreationViewingOptions = {
		displayExpr: 'name',
		items: sortedFeatures,
		value: formData.viewFeature,
		valueExpr: 'name',
		searchEnabled: true,
		onValueChanged: (e: { value: string; }) => validateViewApplicationFeature(e.value)
	};
	const featuresCreationExecutiveOptions = {
		displayExpr: 'name',
		items: sortedFeatures,
		value: formData.executiveFeature,
		valueExpr: 'name',
		searchEnabled: true,
		onValueChanged: (e: { value: string; }) => validateExecutiveFeature(e.value)
	};

	const validateApplicationId = (applicationId: string) => {
		setHasEnteredApplicationId(true);
		if(!applicationId){
		  setIsApplicationIdEmpty(true);
		  return;
		}
		setIsApplicationIdEmpty(false);
	};

	const validateDisplayName = (displayName: string) => {
		const pattern = /^[A-Za-z0-9\s\-()_]{1,100}$/;
		setHasEnteredDisplayName(true);
		if (!displayName) {
		  setIsDisplayNameEmpty(true);
		  setIsDisplayNameValid(true);
		  return;
		}
		setIsDisplayNameEmpty(false);
		setIsDisplayNameValid(pattern.test(displayName));
	};

	const validateAdminFeature = (adminFeature: string) => {
		setHasEnteredAdminFeature(true);
		if(!adminFeature){
		  setIsAdminFeatureEmpty(true);
		  return;
		}
		setIsAdminFeatureEmpty(false);
	};

	const validateViewApplicationFeature = (viewApplicationFeature: string) => {
		setHasEnteredViewApplicationFeature(true);
		if(!viewApplicationFeature){
		  setIsViewApplicationFeatureEmpty(true);
		  return;
		}
		setIsViewApplicationFeatureEmpty(false);
	};

	const validateExecutiveFeature = (executiveReportsFeature: string) => {
		if(executiveReportsFeature){
			setHasEnteredExecutiveReportsFeature(true);
		}
		else {
			setHasEnteredExecutiveReportsFeature(false);
		}
	};

	const setFileContent = (file: File) => {
		setFormData((previousFormData: any) => ({
			...previousFormData,
			file: file
		}));
	};

	const handleFileUpload = (event: any) => {
		const file = event.target.files[0];
		if (file) {
			setFileContent(file);
			setIsValidFileExtension(handleImageExtensionValidation(file.name));
            setFileName(file.name);
		}
	};

	const visibilityEditorOptions = {
		value: formData.isActive
	};

	const fetchUnregisteredApplicationData = async () => {
		const response: ApplicationDto[] = await fetchUnregisteredApplicationsList();
		if (response) {
			var sortedData = sortUnregisteredApplications(response);
			setUnregisteredApplications(sortedData);
		}
	};

	const updateRegisteredApplicationsBannerCount = async () => {
		const updatedRegisteredApplications = await fetchRegisteredApplicationsList();
		if (updatedRegisteredApplications) {
			dispatch(setBannerCount(updatedRegisteredApplications.length));
		}
	};

	const ActivateFieldValidation = () => {
		setHasEnteredDisplayName(true);
		setHasEnteredApplicationId(true);
		setHasEnteredAdminFeature(true);
		setHasEnteredViewApplicationFeature(true);
	};

	const setFeaturesValue = (executiveFeature: boolean) => {
		if(executiveFeature){
			return [
				{ Id: "0", Feature: formData.adminFeature },
				{ Id: "1", Feature: formData.viewFeature },
				{ Id: "2", Feature: formData.executiveFeature }
			];
		}
		else {
			return [
				{ Id: "0", Feature: formData.adminFeature },
				{ Id: "1", Feature: formData.viewFeature }
			];
		}
	};

	useEffect(() => {
		fetchUnregisteredApplicationData();
	}, []);

	const handleSubmit = async () => {
		const isFormValid = (!isApplicationIdEmpty && !isDisplayNameEmpty && isDisplayNameValid &&
			!isAdminFeatureEmpty && !isViewApplicationFeatureEmpty);
		if (!isFormValid) {
			ActivateFieldValidation();
			return;
		}
		setCurrentlySubmitting(true);
		try {
			const requestFormData = new FormData();
			const features = setFeaturesValue(hasEnteredExecutiveReportsFeature);
			requestFormData.append("applicationId",  formData.applicationId);
			for (var i = 0; i < features.length; i++) {
				requestFormData.append("features[" + i + "].Id", features[i].Id);
				requestFormData.append("features[" + i + "].Feature", features[i].Feature);
			}
			requestFormData.append("applicationName",  formData.applicationName);
			requestFormData.append("isActive",  JSON.stringify(formData.isActive));
			requestFormData.append("file",  formData.file);

			const response: any = await registerApplication(requestFormData);
			if (response) {
				showSuccessSnackbar(
					dispatch,
					`Application registered successfully`
				);
				dispatch(refreshApplications(true));
				await updateRegisteredApplicationsBannerCount();
				props.onCancelClick(false);
				props.onRegister();
			}
		} catch (e: any) {
			setError(e);
		}
		setCurrentlySubmitting(false);
	};

	return (
		(unregisteredApplications.length > 0) ? (
		<> 
		<ErrorMessage error={error} />
		<LoadIndicator className='api-loader submitting-loader' visible={currentlySubmitting}/>
		<div className="form-drawer-content">
			<div className="form-container form-scroll-horizontal">
				<Form
					formData={formData}
					readOnly={false}
					showColonAfterLabel={false}
					showValidationSummary={false}
					ref={formRef}
					className=""
				>
					<Item
						dataField="applicationId"
						editorType="dxSelectBox"
						editorOptions={applicationIdOptions}
					>
						<Label text="Application" location="top"/>
					</Item>
					<Item
						dataField="applicationName"
						editorOptions={applicationDisplayNameOptions}
					>
						<Label text="Display Name" location="top"/>
					</Item>
					<Item
						dataField="adminFeature"
						editorType="dxSelectBox"
						editorOptions={featuresCreationAdminOptions}
						cssClass="form-item-spacing"
					>
						<Label text="Admin Feature" location="top"/>
					</Item>
					<Item
						dataField="viewFeature"
						editorType="dxSelectBox"
						editorOptions={featuresCreationViewingOptions}
					>
						<Label text="View Application Feature" location="top"/>
					</Item>
					<Item
						dataField="executiveFeature"
						editorType="dxSelectBox"
						editorOptions={featuresCreationExecutiveOptions}
					>
						<Label text="Executive Reports Feature (optional)" location="top"/>
					</Item>
					<Item
						dataField="image"
					>
						<Label text="Logo" location="top"/>
						<input type="file" ref={logoRef} accept=".jpg,.jpeg,.png" onChange={handleFileUpload} style={{ display: 'none' }}/>
						<button	className="logo-default" onClick={() => logoRef.current?.click()}>Choose File</button>
						{fileName && <span className="logo-default file-name">{fileName}</span>}
						<div className="logo-default logo-info">
							Allowed file extensions: .jpg, .jpeg .png
						</div>
					</Item>
					<Item
						dataField="isActive"
						editorType="dxSwitch"
						cssClass="dx-form-switch"
						editorOptions={visibilityEditorOptions}
					>
						<Label text="Is Active" location="top"/>
					</Item>
					<Item>
					{!isValidFileExtension && 
							<div className="invalid-file-text">
								Invalid file extension.
							</div>
						}
					</Item>
				</Form>
				<div className="invalid-message invalid-application-id-message-creation">
				{isApplicationIdEmpty && hasEnteredApplicationId && (
					<span>Application has to be selected</span>
				)}
				</div>
				<div className="invalid-message invalid-display-name-message-creation">
				{!isDisplayNameValid && hasEnteredDisplayName && (
					<span>Display Name can only contain letters, digits, whitespaces, hyphens, underscores and parenthesis up to 100 characters.</span>
				)}
				{isDisplayNameEmpty && isDisplayNameValid && hasEnteredDisplayName && (
					<span>Display Name cannot be empty</span>
				)}
				</div>
				<div className="invalid-message invalid-admin-feature-message-creation">
				{isAdminFeatureEmpty && hasEnteredAdminFeature &&(
					<span>Admin Feature has to be selected</span>
				)}
				</div>
				<div className="invalid-message invalid-view-application-feature-message-creation">
				{isViewApplicationFeatureEmpty && hasEnteredViewApplicationFeature &&(
					<span>View Application Feature has to be selected</span>
				)}
				</div>
			</div>
			<div className="drawer-footer-buttons">
				<DrawerFooter handleSubmit={handleSubmit} onCancelClick={() => props.onCancelClick(false)} />
			</div>
		</div>
		</>
		) :
		<LoadIndicator className="loading-wheel"/>
	);
};

export default React.memo(ApplicationRegister);